import React, { useEffect, useMemo, useState } from 'react';
//import {makeStyles} from "@material-ui/core/styles";
//import {Button} from "@material-ui/core";
import Show from './connector.show';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConnectors } from '../connectors/connector.actions';
import { fetchUserConnectors } from '../userconnectors/userconnector.actions';
import { prettifyScope } from '../utilities/scopePrettifier';
import './integrations.css';

const normalizeScopes = (scopes) => {
  let parsedScopes = scopes;

  if (typeof scopes === 'string') {
    // Supports comma-delimited or space-delimited OAuth scope strings.
    parsedScopes = scopes.split(/[,\s]+/);
  }

  return Array.isArray(parsedScopes)
    ? parsedScopes
        .map((scope) => {
          if (typeof scope === 'string') {
            const value = scope.trim();
            return value
              ? { value, label: prettifyScope(value) || value }
              : null;
          }

          if (scope && typeof scope === 'object') {
            const value = scope.scope || scope.value || scope.name || scope.id;
            if (!value) {
              return null;
            }

            return {
              value,
              label:
                scope.label ||
                scope.description ||
                prettifyScope(String(value)) ||
                scope.name ||
                value,
            };
          }

          return null;
        })
        .filter(Boolean)
        .filter(
          (scope, index, all) =>
            all.findIndex((item) => item.value === scope.value) === index
        )
    : [];
};

function Index(props) {
  const dispatch = useDispatch();
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [selectedConnectorId, setSelectedConnectorId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [connectionFilter, setConnectionFilter] = useState('all');
  const [permissionSearch, setPermissionSearch] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [configuration, setConfiguration] = useState({});
  const [setupError, setSetupError] = useState('');
  const selectedConnector = (
    useSelector((state) => state.connector?.data) || []
  ).find((item) => item.id === selectedConnectorId);

  const connectors = useSelector((state) => state.connector?.data);
  const userConnectors = useSelector((state) => state.userConnector?.data);

  const connectedIds = useMemo(
    () =>
      new Set(
        (userConnectors || [])
          .filter((item) => item.setupStatus !== 'action_required')
          .map((item) => item.connectorId)
      ),
    [userConnectors]
  );

  const visibleConnectors = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return (connectors || [])
      .filter((connector) => {
        const isAlreadyConnected = connectedIds.has(connector.id);
        const fitsStep =
          !props.step ||
          (!isAlreadyConnected &&
            connector.steps &&
            connector.steps.includes(props.step));
        const fitsSearch =
          !query || (connector.name || '').toLowerCase().includes(query);

        const fitsConnection =
          connectionFilter === 'all' ||
          (connectionFilter === 'connected'
            ? isAlreadyConnected
            : !isAlreadyConnected);
        return fitsStep && fitsSearch && fitsConnection;
      })
      .sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
        })
      );
  }, [connectors, connectedIds, props.step, searchTerm, connectionFilter]);

  const handleConnectorClick = (connector) => {
    const scopes = normalizeScopes(connector?.scope);

    setConfiguration({});
    setSetupError('');
    if (!scopes.length && !connector.connectionSetup?.fields?.length) {
      props.getConnector(connector.id);
      return;
    }

    setScopeOptions(scopes);
    setPermissionSearch('');
    setSelectedOnly(false);
    setSelectedScopes([]);
    setSelectedConnectorId(connector.id);
    setScopeDialogOpen(true);
  };

  const handleScopeToggle = (scopeValue) => {
    setSelectedScopes((currentScopes) => {
      if (currentScopes.includes(scopeValue)) {
        return currentScopes.filter((scope) => scope !== scopeValue);
      }

      return [...currentScopes, scopeValue];
    });
  };

  const handleScopeConfirm = () => {
    if (!selectedConnectorId) {
      return;
    }

    for (const field of selectedConnector?.connectionSetup?.fields || []) {
      const value = String(configuration[field.name] || '').trim();
      if (
        (field.required || value) &&
        !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(value)
      ) {
        setSetupError(`Enter a valid ${field.label || field.name}`);
        return;
      }
    }
    props.getConnector(
      selectedConnectorId,
      selectedScopes.join(' '),
      configuration
    );
    setScopeDialogOpen(false);
    setScopeOptions([]);
    setSelectedScopes([]);
    setSelectedConnectorId(null);
  };

  const handleScopeClose = () => {
    setScopeDialogOpen(false);
    setScopeOptions([]);
    setSelectedScopes([]);
    setSelectedConnectorId(null);
  };

  useEffect(() => {
    dispatch(fetchConnectors());
    dispatch(fetchUserConnectors());
  }, [dispatch]);

  return (
    <Box className="integrations-workspace">
      {!props.step && (
        <header className="integrations-hero">
          <span>YOUR CONNECTED WORKSPACE</span>
          <h1>Your tools. More possibilities.</h1>
          <p>
            Bring your tools and services into LifeTagger. You choose what each
            connection can access.
          </p>
        </header>
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
        }}
      >
        <div
          className="integration-status-filters"
          role="group"
          aria-label="Filter connections"
        >
          {['all', 'connected', 'available'].map((filter) => (
            <button
              type="button"
              key={filter}
              aria-pressed={connectionFilter === filter}
              onClick={() => setConnectionFilter(filter)}
            >
              {filter === 'all'
                ? 'All integrations'
                : filter === 'connected'
                ? 'Connected'
                : 'Not connected'}{' '}
              (
              {
                (connectors || []).filter(
                  (connector) =>
                    filter === 'all' ||
                    (filter === 'connected'
                      ? connectedIds.has(connector.id)
                      : !connectedIds.has(connector.id))
                ).length
              }
              )
            </button>
          ))}
        </div>
        <TextField
          size="small"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search integrations"
          inputProps={{ 'aria-label': 'Search integrations' }}
          sx={{ minWidth: 260, maxWidth: 360, width: '100%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 2,
        }}
      >
        {visibleConnectors.map((connector) => (
          <Show
            key={connector.id}
            connector={connector}
            connected={connectedIds.has(connector.id)}
            setupRequired={(userConnectors || []).some(
              (item) =>
                item.connectorId === connector.id &&
                item.setupStatus === 'action_required'
            )}
            onClick={() => {
              if (props.callBack) {
                props.callBack(connector.id);
              } else {
                handleConnectorClick(connector);
              }
            }}
          />
        ))}
      </Box>

      {visibleConnectors.length === 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No connectors match your filters.
          </Typography>
        </Box>
      )}

      <Dialog
        open={scopeDialogOpen}
        onClose={handleScopeClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="permissions-title"
        PaperProps={{ className: 'integration-permissions' }}
      >
        <DialogTitle id="permissions-title">
          <small>CONNECT {selectedConnector?.name}</small>Choose what to share.
        </DialogTitle>
        <div className="permission-toolbar">
          <input
            type="search"
            aria-label="Search permissions"
            placeholder="Find a permission, e.g. contacts…"
            value={permissionSearch}
            onChange={(event) => setPermissionSearch(event.target.value)}
          />
          <button
            type="button"
            disabled={
              !scopeOptions.length ||
              scopeOptions.every((scope) =>
                selectedScopes.includes(scope.value)
              )
            }
            title="Select every permission, including sensitive access, even when filtered"
            onClick={() =>
              setSelectedScopes(scopeOptions.map((scope) => scope.value))
            }
          >
            Select all ({scopeOptions.length})
          </button>
          <button
            disabled={!selectedScopes.length}
            onClick={() => {
              setSelectedScopes([]);
              setSelectedOnly(false);
            }}
          >
            Clear all
          </button>
        </div>
        <div
          className="permission-views"
          role="group"
          aria-label="Permission list view"
        >
          <button
            type="button"
            aria-pressed={!selectedOnly}
            onClick={() => {
              setSelectedOnly(false);
              setPermissionSearch('');
            }}
          >
            All permissions ({scopeOptions.length})
          </button>
          <button
            type="button"
            aria-pressed={selectedOnly}
            onClick={() => setSelectedOnly(true)}
          >
            Selected ({selectedScopes.length})
          </button>
        </div>
        <DialogContent dividers>
          {(selectedConnector?.connectionSetup?.fields || []).map((field) => (
            <TextField
              key={field.name}
              fullWidth
              margin="normal"
              label={field.label}
              required={field.required}
              value={configuration[field.name] || ''}
              helperText={field.help}
              inputProps={{ maxLength: 63 }}
              onChange={(event) => {
                setConfiguration((current) => ({
                  ...current,
                  [field.name]: event.target.value,
                }));
                setSetupError('');
              }}
            />
          ))}
          {setupError && (
            <Typography role="alert" color="error">
              {setupError}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose the access this connection needs. Nothing is granted until
            you authorize the connection.
          </Typography>
          {Object.entries(
            scopeOptions
              .filter(
                (scope) =>
                  (!selectedOnly || selectedScopes.includes(scope.value)) &&
                  `${scope.label} ${scope.value}`
                    .toLowerCase()
                    .includes(permissionSearch.toLowerCase())
              )
              .reduce((groups, scope) => {
                const group = /sensitive|restricted/i.test(scope.value)
                  ? 'Sensitive data'
                  : scope.value.includes('.')
                  ? scope.value.split('.')[0].toUpperCase()
                  : 'General';
                (groups[group] = groups[group] || []).push(scope);
                return groups;
              }, {})
          ).map(([group, scopes]) => (
            <section key={group} className="permission-group">
              <header>
                <h3>
                  {group} <small>{scopes.length}</small>
                </h3>
                {group !== 'Sensitive data' && (
                  <FormControlLabel
                    label="Select group"
                    control={
                      <Checkbox
                        size="small"
                        checked={scopes.every((scope) =>
                          selectedScopes.includes(scope.value)
                        )}
                        indeterminate={
                          scopes.some((scope) =>
                            selectedScopes.includes(scope.value)
                          ) &&
                          !scopes.every((scope) =>
                            selectedScopes.includes(scope.value)
                          )
                        }
                        onChange={() =>
                          setSelectedScopes((current) =>
                            scopes.every((scope) =>
                              current.includes(scope.value)
                            )
                              ? current.filter(
                                  (value) =>
                                    !scopes.some(
                                      (scope) => scope.value === value
                                    )
                                )
                              : [
                                  ...new Set([
                                    ...current,
                                    ...scopes.map((scope) => scope.value),
                                  ]),
                                ]
                          )
                        }
                      />
                    }
                  />
                )}
              </header>
              <FormGroup>
                {scopes.map((scope) => (
                  <FormControlLabel
                    key={scope.value}
                    className={
                      selectedScopes.includes(scope.value)
                        ? 'permission-row selected'
                        : 'permission-row'
                    }
                    control={
                      <Checkbox
                        checked={selectedScopes.includes(scope.value)}
                        onChange={() => handleScopeToggle(scope.value)}
                      />
                    }
                    label={
                      <span>
                        <strong>{scope.label}</strong>
                        <small>{scope.value}</small>
                        {/sensitive|restricted|write|delete|manage/i.test(
                          scope.value
                        ) && (
                          <em>
                            {/sensitive|restricted/i.test(scope.value)
                              ? 'Sensitive data'
                              : 'Can make changes'}
                          </em>
                        )}
                      </span>
                    }
                  />
                ))}
              </FormGroup>
            </section>
          ))}
          {!scopeOptions.some(
            (scope) =>
              (!selectedOnly || selectedScopes.includes(scope.value)) &&
              `${scope.label} ${scope.value}`
                .toLowerCase()
                .includes(permissionSearch.toLowerCase())
          ) && (
            <div className="permission-empty">
              <p>
                {selectedOnly && !selectedScopes.length
                  ? 'No permissions selected yet.'
                  : 'No permissions match your search.'}
              </p>
              <Button
                onClick={() => {
                  setSelectedOnly(false);
                  setPermissionSearch('');
                }}
              >
                Show all permissions
              </Button>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <span className="permission-count">
            {selectedScopes.length} permissions selected
          </span>
          <Button onClick={handleScopeClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleScopeConfirm}
            disabled={scopeOptions.length > 0 && selectedScopes.length === 0}
          >
            Continue to {selectedConnector?.name}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Index;
